import { useState, useCallback } from "react";
import { message } from "antd";
import {
  getCorrals,
  createCorral,
  updateCorral as updateCorralApi,
  deleteCorral as deleteCorralApi,
  autoAssignCorral,
  manualAssignCorral,
  unassignCorral as unassignCorralApi,
  getCorralSummary,
  getCorralAssignments,
  getRegistrationsWithBib,
  getEventBibRanges,
} from "../Services/corralService";
import {
  CorralDefinition,
  CorralParticipant,
  CreateCorralPayload,
  CorralSummary,
  CorralAssignment,
} from "../types/corral.types";

export interface BibRangeInfo {
  start: number;
  end: number;
  ticket_id?: string;
  ticket_name?: string;
}

export function useCorral() {
  const [corrals, setCorrals] = useState<CorralDefinition[]>([]);
  const [participants, setParticipants] = useState<CorralParticipant[]>([]);
  const [assignments, setAssignments] = useState<CorralAssignment[]>([]);
  const [summary, setSummary] = useState<CorralSummary | null>(null);
  const [bibRanges, setBibRanges] = useState<BibRangeInfo[]>([]);
  const [loadingCorrals, setLoadingCorrals] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [total, setTotal] = useState(0);
  const [assignmentsTotal, setAssignmentsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const pageSize = 500;
  const assignmentsPageSize = 50;

  const fetchBibRanges = useCallback(
    async (event_id: string, distance?: string) => {
      try {
        const res = await getEventBibRanges(event_id, distance);
        const raw: any[] = res.data?.data?.data ?? res.data?.data ?? [];
        const normalized: BibRangeInfo[] = raw.map((r) => ({
          start: r.start_number ?? r.bib_start ?? r.start,
          end: r.end_number ?? r.bib_end ?? r.end,
          ticket_id: r.ticket_id,
          ticket_name: r.ticket_name ?? r.ticket?.name,
        }));
        setBibRanges(normalized);
        return normalized;
      } catch {
        setBibRanges([]);
        return [];
      }
    },
    [],
  );

  const fetchCorrals = useCallback(
    async (event_id: string, distance?: string) => {
      if (!event_id) return;
      setLoadingCorrals(true);
      try {
        const res = await getCorrals(event_id, distance);
        const data: CorralDefinition[] =
          res.data?.data?.data ?? res.data?.data ?? [];
        setCorrals(data);
        return data;
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to load corrals.",
        );
        return [];
      } finally {
        setLoadingCorrals(false);
      }
    },
    [],
  );

  const fetchParticipants = useCallback(
    async (event_id: string, distance: string, page = 1) => {
      if (!event_id || !distance) return;

      setLoadingParticipants(true);
      try {
        const res = await getRegistrationsWithBib(
          event_id,
          distance,
          page,
          pageSize,
          { "filter[status][eq]": "placed" }
        );
        const list: CorralParticipant[] =
          res.data?.data?.data ?? res.data?.data ?? [];
        const apiTotal = res.data?.data?.total ?? list.length;
        setParticipants(list);
        setTotal(apiTotal);
        setCurrentPage(page);
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to load participants.",
        );
      } finally {
        setLoadingParticipants(false);
      }
    },
    [],
  );

  const fetchSummary = useCallback(
    async (event_id: string, distance?: string) => {
      if (!event_id) return;
      setLoadingSummary(true);
      try {
        const res = await getCorralSummary(event_id, distance);
        const data: CorralSummary = res.data?.data ?? null;
        setSummary(data);
        return data;
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to load summary.",
        );
        return null;
      } finally {
        setLoadingSummary(false);
      }
    },
    [],
  );

  const fetchAssignments = useCallback(
    async (
      event_id: string,
      distance?: string,
      corral_id?: string,
      page = 1,
    ) => {
      if (!event_id) return;
      setLoadingAssignments(true);
      try {
        const res = await getCorralAssignments(
          event_id,
          distance,
          corral_id,
          page,
          assignmentsPageSize,
        );
        const list: CorralAssignment[] =
          res.data?.data?.data ?? res.data?.data ?? [];
        const apiTotal = res.data?.data?.total ?? list.length;
        setAssignments(list);
        setAssignmentsTotal(apiTotal);
        setAssignmentsPage(page);
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to load assignments.",
        );
      } finally {
        setLoadingAssignments(false);
      }
    },
    [],
  );

  const addCorral = useCallback(
    async (payload: CreateCorralPayload): Promise<boolean> => {
      try {
        const res = await createCorral(payload);
        const created: CorralDefinition = res.data?.data ?? res.data;
        setCorrals((prev) => [...prev, created]);
        message.success(`Corral ${payload.name} created.`);
        return true;
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to create corral.",
        );
        return false;
      }
    },
    [],
  );

  const editCorral = useCallback(
    async (
      id: string,
      payload: Partial<CreateCorralPayload>,
    ): Promise<boolean> => {
      try {
        const res = await updateCorralApi(id, payload);
        const updated: CorralDefinition = res.data?.data ?? res.data;
        setCorrals((prev) => prev.map((c) => (c.id === id ? updated : c)));
        message.success("Corral updated.");
        return true;
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to update corral.",
        );
        return false;
      }
    },
    [],
  );

  const removeCorral = useCallback(
    async (
      id: string,
      event_id: string,
      distance?: string,
    ): Promise<boolean> => {
      try {
        await deleteCorralApi(id);
        setCorrals((prev) => prev.filter((c) => c.id !== id));
        message.success("Corral deleted.");
        if (event_id) {
          fetchSummary(event_id, distance);
          fetchAssignments(event_id, distance);
        }
        return true;
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to delete corral.",
        );
        return false;
      }
    },
    [fetchSummary, fetchAssignments],
  );

  const runAutoAssign = useCallback(
    async (event_id: string, distance: string) => {
      setAssigning(true);
      try {
        const res = await autoAssignCorral(event_id, distance);
        const count = res.data?.data?.assigned_count ?? 0;
        message.success(`${count} registration(s) auto-assigned to corrals.`);
        await Promise.all([
          fetchCorrals(event_id, distance),
          fetchSummary(event_id, distance),
          fetchAssignments(event_id, distance),
        ]);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Auto-assign failed.");
      } finally {
        setAssigning(false);
      }
    },
    [fetchCorrals, fetchSummary, fetchAssignments],
  );

  const runManualAssign = useCallback(
    async (
      registration_id: string,
      corral_id: string,
      event_id: string,
      distance: string,
    ) => {
      try {
        await manualAssignCorral({ registration_id, corral_id });
        message.success("Corral assigned.");
        await Promise.all([
          fetchAssignments(event_id, distance, undefined, assignmentsPage),
          fetchSummary(event_id, distance),
        ]);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Manual assign failed.");
      }
    },
    [fetchAssignments, fetchSummary, assignmentsPage],
  );

  const runUnassign = useCallback(
    async (registration_id: string, event_id: string, distance: string) => {
      try {
        await unassignCorralApi(registration_id);
        message.success("Corral unassigned.");
        await Promise.all([
          fetchAssignments(event_id, distance, undefined, assignmentsPage),
          fetchSummary(event_id, distance),
        ]);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Unassign failed.");
      }
    },
    [fetchAssignments, fetchSummary, assignmentsPage],
  );

  return {
    corrals,
    participants,
    assignments,
    summary,
    bibRanges,
    loadingCorrals,
    loadingParticipants,
    loadingAssignments,
    loadingSummary,
    assigning,
    total,
    assignmentsTotal,
    currentPage,
    assignmentsPage,
    pageSize,
    assignmentsPageSize,
    fetchCorrals,
    fetchParticipants,
    fetchSummary,
    fetchAssignments,
    fetchBibRanges,
    addCorral,
    editCorral,
    removeCorral,
    runAutoAssign,
    runManualAssign,
    runUnassign,
  };
}
